import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import {
	MAT_DIALOG_DATA,
	MatDialog,
	MatDialogModule,
	MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { PaymentProcessingService } from '@lmc-app/core/services/payment-processing.service';
import {
	ArrangeBankAccount,
	PromiseToPay,
} from '@lmc-app/core/services/promise-to-pay.service';
import {
	achStatusLabel,
	ManualBankAccount,
} from '@lmc-app/loan-servicing/payment-processing/create/manual-transaction-dialog.model';
import {
	CustomBankAccount,
	CustomBankAccountDialogComponent,
	CustomBankAccountDialogData,
} from '@lmc-app/loan-servicing/payment-processing/create/custom-bank-account-dialog.component';
import { TransactionPreviewStatus } from '@lmc-app/shared/enum/transaction-preview-status.enum';
import { FormatUtil } from '@lmc-app/shared/utils/format.util';

export interface PromiseToPayBankAccountDialogData {
	promise: PromiseToPay;
}

/**
 * The account to charge; null when the loan turned out to have an active account, so
 * there is nothing to pick; undefined when the operator cancelled.
 */
export type PromiseToPayBankAccountDialogResult =
	| ArrangeBankAccount
	| null
	| undefined;

const ACTIVE_ACH_STATUS = 0;

/** Value of the dropdown's Custom option; opens the custom account form, never selected. */
const CUSTOM_OPTION = 'custom';

/**
 * Asks for the account to charge when arranging a manual_debit promise whose loan has no
 * active bank account. Emits null when no pick is needed and undefined when cancelled.
 */
export function pickArrangeBankAccount(
	dialog: MatDialog,
	promise: PromiseToPay
): Observable<PromiseToPayBankAccountDialogResult> {
	if (promise.hasActiveBankAccount === true) return of(null);
	return dialog
		.open<
			PromiseToPayBankAccountDialogComponent,
			PromiseToPayBankAccountDialogData,
			PromiseToPayBankAccountDialogResult
		>(PromiseToPayBankAccountDialogComponent, {
			width: '480px',
			autoFocus: false,
			data: { promise },
		})
		.afterClosed();
}

/**
 * Picks the account to charge when a manual_debit promise's loan has no active bank
 * account: the customer's expired or disabled accounts, or a custom one, the same way the
 * manual transaction dialog's Banking Account dropdown does.
 */
@Component({
	selector: 'app-promise-to-pay-bank-account-dialog',
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		FormlyModule,
		FormlyMaterialModule,
		MatDialogModule,
		MatButtonModule,
		MatProgressSpinnerModule,
	],
	templateUrl: './promise-to-pay-bank-account-dialog.component.html',
	styleUrls: ['./promise-to-pay-bank-account-dialog.component.scss'],
})
export class PromiseToPayBankAccountDialogComponent
	implements OnInit, OnDestroy
{
	readonly FormatUtil = FormatUtil;
	readonly form = new FormGroup({});
	model: { bankAccount: ManualBankAccount | null } = { bankAccount: null };
	private bankAccounts: ManualBankAccount[] = [];
	/** Last real account picked; restored when the Custom option is chosen. */
	private selectedAccount: ManualBankAccount | null = null;
	fields: FormlyFieldConfig[] = [];

	isLoading = true;
	loadError: string | null = null;

	private readonly destroy$ = new Subject<void>();

	constructor(
		private readonly dialogRef: MatDialogRef<
			PromiseToPayBankAccountDialogComponent,
			PromiseToPayBankAccountDialogResult
		>,
		private readonly paymentProcessingService: PaymentProcessingService,
		private readonly dialog: MatDialog,
		@Inject(MAT_DIALOG_DATA)
		public readonly data: PromiseToPayBankAccountDialogData
	) {}

	ngOnInit(): void {
		this.paymentProcessingService
			.lookupLoan(this.data.promise.loanNumber)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: ({ bankAccounts }) => {
					const accounts = bankAccounts ?? [];
					// Only reached without a known answer (e.g. a deposit switched to a
					// manual debit); an active account needs no pick.
					if (accounts.some((a) => a.achStatus === ACTIVE_ACH_STATUS)) {
						this.dialogRef.close(null);
						return;
					}
					this.showAccounts(accounts);
				},
				error: (err) => {
					// No account on file: only a custom one can be entered.
					if (err?.status === 404) {
						this.showAccounts([]);
						return;
					}
					this.isLoading = false;
					this.loadError =
						err?.error?.message ?? 'Failed to load the bank accounts.';
				},
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private showAccounts(accounts: ManualBankAccount[]): void {
		this.isLoading = false;
		this.bankAccounts = accounts;
		this.selectedAccount = accounts[0] ?? null;
		this.model = { bankAccount: this.selectedAccount };
		this.fields = this.buildFields();
	}

	private accountOptions(): {
		value: ManualBankAccount | string;
		label: string;
	}[] {
		return [
			...this.bankAccounts.map((account) => ({
				value: account as ManualBankAccount | string,
				label: this.bankAccountLabel(account),
			})),
			{ value: CUSTOM_OPTION, label: 'Custom account…' },
		];
	}

	private buildFields(): FormlyFieldConfig[] {
		return [
			{
				key: 'bankAccount',
				type: 'select',
				props: {
					label: 'Banking Account',
					placeholder: 'Select bank account',
					required: true,
					appearance: 'outline',
					options: this.accountOptions(),
					// The Custom option opens the form and leaves the current selection.
					change: (field) => {
						const value = field.formControl?.value;
						if (value !== CUSTOM_OPTION) {
							this.selectedAccount = value;
							return;
						}
						field.formControl!.setValue(this.selectedAccount);
						this.openCustomBankAccount(field);
					},
				},
				validators: {
					valid: {
						expression: (c: { value: ManualBankAccount | null }) =>
							!c.value || c.value.status === TransactionPreviewStatus.Valid,
						message: (_: unknown, field: FormlyFieldConfig) =>
							`Customer information is incomplete in NLS: ${
								field.formControl?.value?.error ?? ''
							}`,
					},
				},
			},
		];
	}

	private openCustomBankAccount(field: FormlyFieldConfig): void {
		this.dialog
			.open<
				CustomBankAccountDialogComponent,
				CustomBankAccountDialogData,
				CustomBankAccount
			>(CustomBankAccountDialogComponent, {
				width: '480px',
				maxWidth: '90vw',
				autoFocus: 'first-heading',
				data: {
					loanNumber: this.data.promise.loanNumber,
					payeeName: this.data.promise.customerName ?? '',
				},
			})
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((entered) => {
				if (!entered) return;
				// Pick the existing entry when the same account is already listed.
				let account = this.bankAccounts.find(
					(a) =>
						a.institutionCode === entered.institutionCode &&
						a.transitNumber === entered.transitNumber &&
						a.accountNumber === entered.accountNumber
				);
				if (!account) {
					// Customer-data verdict is per customer, so it is taken from a listed account.
					const listed =
						this.bankAccounts.find((a) => !a.custom) ?? this.bankAccounts[0];
					account = {
						...entered,
						loanNumber: null,
						achStatus: null,
						status: listed?.status ?? TransactionPreviewStatus.Valid,
						error: listed?.error ?? null,
						custom: true,
					};
					this.bankAccounts = [...this.bankAccounts, account];
					field.props!.options = this.accountOptions();
				}
				this.selectedAccount = account;
				field.formControl?.setValue(account);
			});
	}

	/** Same label as the manual transaction dialog's Banking Account dropdown. */
	private bankAccountLabel(account: ManualBankAccount): string {
		const label = `${account.institutionCode}-${account.transitNumber} / ${account.accountNumber}`;
		if (account.custom) return `${label} · Custom`;
		return account.achStatus == null
			? label
			: `${label} · ${achStatusLabel(account.achStatus)}`;
	}

	onConfirm(): void {
		if (this.form.invalid || !this.model.bankAccount) {
			this.form.markAllAsTouched();
			return;
		}
		const { institutionCode, transitNumber, accountNumber } =
			this.model.bankAccount;
		this.dialogRef.close({ institutionCode, transitNumber, accountNumber });
	}

	onCancel(): void {
		this.dialogRef.close();
	}
}
